/**
 * 🅿️ [Phase 101 — 2026-07-09] Parking Refonte simple.
 *
 * Modèle : liste d'unités nommées manuellement par la propriétaire, chacune
 * assignée (ou non) à une place de parking (texte libre).
 *
 * Accès :
 *   • Lecture : tout utilisateur connecté.
 *   • Écriture (mode édition) : propriétaire uniquement.
 *
 * UX :
 *   • Grille de cartes cliquables (2/3/4/5 colonnes selon largeur écran).
 *   • Clic sur unité assignée → modal grande "Place de parking : X".
 *   • Section « Non assignées » sous la grille principale.
 *   • Mode édition (Owner-only, toggle en haut) : add / edit inline / delete /
 *     drag-reorder (sur la grille principale).
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  MapPin, Plus, Pencil, Trash2, X, Save, GripVertical, Settings2, Info,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API_ROOT = `${API_URL}/api/parking-units`;

export default function Parking() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({ assignees: [], non_assignees: [], can_edit: false, total: 0 });
  const [selectedUnit, setSelectedUnit] = useState(null);     // unité affichée en drawer lecture
  const [editingUnit, setEditingUnit] = useState(null);      // { id?, nom_manuel, place_parking, notes } — null = pas d'édition
  const [showAddForm, setShowAddForm] = useState(false);
  const [modeEdition, setModeEdition] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);  // unit à supprimer

  const isOwner = data.can_edit;

  // Support de l'ancienne route /parking/admin → mode_edition=true
  useEffect(() => {
    if (searchParams.get('mode_edition') === 'true' && isOwner) {
      setModeEdition(true);
      // Nettoyer l'URL sans reload
      searchParams.delete('mode_edition');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, isOwner]);

  const fetchUnits = async () => {
    try {
      setLoading(true);
      const r = await axios.get(API_ROOT);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors du chargement.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnits();
  }, []);

  // ─── Actions Owner ────────────────────────────────────────────────────────

  const handleSave = async (unit) => {
    if (!unit.nom_manuel || !unit.nom_manuel.trim()) {
      toast.error('Le nom manuel est obligatoire.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nom_manuel: unit.nom_manuel.trim(),
        place_parking: unit.place_parking ? unit.place_parking.trim() : null,
        notes: unit.notes ? unit.notes.trim() : null,
      };
      if (unit.id) {
        await axios.put(`${API_ROOT}/${unit.id}`, payload);
        toast.success('Unité mise à jour.');
      } else {
        await axios.post(API_ROOT, payload);
        toast.success('Unité créée.');
      }
      setEditingUnit(null);
      setShowAddForm(false);
      setSelectedUnit(null);
      await fetchUnits();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setSaving(true);
    try {
      await axios.delete(`${API_ROOT}/${confirmDelete.id}`);
      toast.success('Unité supprimée.');
      setConfirmDelete(null);
      setSelectedUnit(null);
      await fetchUnits();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la suppression.');
    } finally {
      setSaving(false);
    }
  };

  const handleReorder = async (newAssignees) => {
    // Optimistic update de l'ordre
    setData(prev => ({ ...prev, assignees: newAssignees }));
    try {
      await axios.put(`${API_ROOT}/reorder`, {
        order: [...newAssignees, ...data.non_assignees].map(u => u.id),
      });
    } catch (e) {
      toast.error('Erreur lors du réordonnancement — rechargement.');
      await fetchUnits();
    }
  };

  // ─── Rendu ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto" data-testid="parking-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/3" />
          <div className="h-4 bg-slate-100 rounded w-2/3" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <div key={i} className="h-32 bg-slate-100 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="parking-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <MapPin className="w-7 h-7 text-blue-600" />
            Parking
          </h1>
          <p className="text-sm text-slate-600 mt-1 flex items-center gap-1.5">
            <Info className="w-4 h-4 text-slate-400" />
            Cliquez sur l&apos;unité pour voir la place de parking attitrée.
          </p>
        </div>
        {isOwner && (
          <div className="flex items-center gap-2">
            <Button
              variant={modeEdition ? 'default' : 'outline'}
              size="sm"
              onClick={() => setModeEdition(!modeEdition)}
              data-testid="btn-mode-edition"
            >
              <Settings2 className="w-4 h-4 mr-1.5" />
              {modeEdition ? 'Quitter le mode édition' : 'Mode édition'}
            </Button>
          </div>
        )}
      </div>

      {/* Info banner mode édition */}
      {modeEdition && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800 flex items-start gap-2" data-testid="mode-edition-banner">
          <Settings2 className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <strong>Mode édition activé</strong> — vous pouvez ajouter, modifier ou supprimer des unités.
            Faites glisser (poignée à gauche) pour réordonner.
          </div>
        </div>
      )}

      {/* Section principale : unités assignées */}
      <UnitesGrid
        title="Unités assignées"
        units={data.assignees}
        modeEdition={modeEdition}
        isOwner={isOwner}
        onCardClick={setSelectedUnit}
        onEdit={setEditingUnit}
        onDelete={setConfirmDelete}
        onReorder={handleReorder}
        emptyText="Aucune unité assignée à une place pour le moment."
      />

      {/* Section : unités non assignées */}
      {data.non_assignees.length > 0 && (
        <div className="mt-8">
          <UnitesGrid
            title="Non assignées"
            units={data.non_assignees}
            modeEdition={modeEdition}
            isOwner={isOwner}
            onCardClick={setSelectedUnit}
            onEdit={setEditingUnit}
            onDelete={setConfirmDelete}
            variant="unassigned"
          />
        </div>
      )}

      {/* Bouton "+" ajouter (Owner + mode édition) */}
      {isOwner && modeEdition && (
        <div className="mt-6 flex justify-center">
          <Button
            variant="outline"
            size="lg"
            onClick={() => { setShowAddForm(true); setEditingUnit({ nom_manuel: '', place_parking: '', notes: '' }); }}
            data-testid="btn-add-unite"
            className="border-dashed border-2"
          >
            <Plus className="w-5 h-5 mr-1.5" />
            Ajouter une unité
          </Button>
        </div>
      )}

      {/* Modal lecture (drawer info) */}
      {selectedUnit && !editingUnit && (
        <Dialog open={!!selectedUnit} onOpenChange={(o) => !o && setSelectedUnit(null)}>
          <DialogContent className="max-w-md" data-testid="dialog-unite-detail">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <MapPin className="w-5 h-5 text-blue-600" />
                {selectedUnit.nom_manuel}
              </DialogTitle>
              <DialogDescription>Détails de l&apos;unité</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {selectedUnit.place_parking ? (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 text-center" data-testid="place-parking-value">
                  <p className="text-sm font-medium text-blue-700 mb-1">Place de parking</p>
                  <p className="text-5xl font-bold text-blue-900 leading-none">{selectedUnit.place_parking}</p>
                </div>
              ) : (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center">
                  <p className="text-sm text-slate-500">Non assignée à une place</p>
                </div>
              )}
              {selectedUnit.notes && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900" data-testid="unite-notes">
                  <strong>Notes :</strong> {selectedUnit.notes}
                </div>
              )}
            </div>
            <DialogFooter className="gap-2 flex-col sm:flex-row">
              {isOwner && (
                <Button
                  variant="outline"
                  onClick={() => setEditingUnit(selectedUnit)}
                  data-testid="btn-edit-unite"
                >
                  <Pencil className="w-4 h-4 mr-1.5" />
                  Modifier
                </Button>
              )}
              <Button onClick={() => setSelectedUnit(null)} data-testid="btn-close-detail">
                Fermer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal formulaire édition/création */}
      {editingUnit && (
        <UniteFormDialog
          unit={editingUnit}
          onChange={setEditingUnit}
          onCancel={() => { setEditingUnit(null); setShowAddForm(false); }}
          onSave={handleSave}
          saving={saving}
          isNew={!editingUnit.id}
        />
      )}

      {/* Confirmation suppression */}
      {confirmDelete && (
        <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
          <DialogContent data-testid="dialog-confirm-delete">
            <DialogHeader>
              <DialogTitle>Supprimer cette unité ?</DialogTitle>
              <DialogDescription>
                <strong>{confirmDelete.nom_manuel}</strong> sera supprimée définitivement. Cette action est irréversible.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDelete(null)} disabled={saving} data-testid="btn-cancel-delete">
                Annuler
              </Button>
              <Button variant="destructive" onClick={handleDelete} disabled={saving} data-testid="btn-confirm-delete">
                {saving ? 'Suppression…' : 'Oui, supprimer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


// ─── Sous-composants ──────────────────────────────────────────────────────────


function UnitesGrid({
  title, units, modeEdition, isOwner, onCardClick, onEdit, onDelete, onReorder,
  variant = 'assigned', emptyText,
}) {
  const dragIdRef = useRef(null);

  const handleDragStart = (idx) => {
    dragIdRef.current = idx;
  };
  const handleDragOver = (e) => { e.preventDefault(); };
  const handleDrop = (targetIdx) => {
    if (!onReorder || dragIdRef.current === null || dragIdRef.current === targetIdx) return;
    const next = [...units];
    const [moved] = next.splice(dragIdRef.current, 1);
    next.splice(targetIdx, 0, moved);
    onReorder(next);
    dragIdRef.current = null;
  };

  return (
    <section data-testid={`section-${variant}`}>
      <h2 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${variant === 'unassigned' ? 'text-slate-500' : 'text-slate-800'}`}>
        {title}
        <Badge variant="outline" className="text-xs">{units.length}</Badge>
      </h2>
      {units.length === 0 && emptyText ? (
        <p className="text-sm text-slate-400 italic py-6 text-center bg-slate-50 rounded-lg border border-dashed" data-testid="grid-empty">
          {emptyText}
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {units.map((u, idx) => (
            <UniteCard
              key={u.id}
              unit={u}
              variant={variant}
              modeEdition={modeEdition}
              isOwner={isOwner}
              onClick={() => onCardClick(u)}
              onEdit={() => onEdit(u)}
              onDelete={() => onDelete(u)}
              draggable={modeEdition && variant === 'assigned' && !!onReorder}
              onDragStart={() => handleDragStart(idx)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(idx)}
            />
          ))}
        </div>
      )}
    </section>
  );
}


function UniteCard({
  unit, variant, modeEdition, isOwner, onClick, onEdit, onDelete,
  draggable, onDragStart, onDragOver, onDrop,
}) {
  const isUnassigned = variant === 'unassigned';
  return (
    <div
      className={`relative rounded-lg border-2 p-4 transition-all cursor-pointer group ${
        isUnassigned
          ? 'bg-slate-50 border-slate-200 hover:border-slate-400'
          : 'bg-white border-blue-200 hover:border-blue-500 hover:shadow-md'
      }`}
      onClick={onClick}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-testid={`unite-card-${unit.id}`}
    >
      {modeEdition && draggable && (
        <GripVertical className="absolute top-2 left-2 w-4 h-4 text-slate-400 cursor-grab" aria-label="Réordonner" />
      )}
      <div className="flex flex-col items-center text-center">
        <MapPin className={`w-8 h-8 mb-2 ${isUnassigned ? 'text-slate-400' : 'text-blue-500'}`} />
        <p className={`font-semibold text-sm break-words ${isUnassigned ? 'text-slate-600' : 'text-slate-900'}`}>
          {unit.nom_manuel}
        </p>
        {unit.place_parking ? (
          <p className="text-xs font-medium text-blue-700 mt-1">Place {unit.place_parking}</p>
        ) : (
          <p className="text-xs text-slate-400 italic mt-1">non assignée</p>
        )}
      </div>
      {modeEdition && isOwner && (
        <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="p-1 rounded bg-white/90 hover:bg-slate-100 shadow-sm border"
            data-testid={`btn-edit-card-${unit.id}`}
            aria-label="Modifier"
          >
            <Pencil className="w-3.5 h-3.5 text-slate-700" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1 rounded bg-white/90 hover:bg-red-50 shadow-sm border"
            data-testid={`btn-delete-card-${unit.id}`}
            aria-label="Supprimer"
          >
            <Trash2 className="w-3.5 h-3.5 text-red-600" />
          </button>
        </div>
      )}
    </div>
  );
}


function UniteFormDialog({ unit, onChange, onCancel, onSave, saving, isNew }) {
  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onCancel()}>
      <DialogContent className="max-w-md" data-testid="dialog-unite-form">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Ajouter une unité' : 'Modifier l\'unité'}</DialogTitle>
          <DialogDescription>
            {isNew
              ? 'Donnez un nom à l\'unité et associez-la à une place de parking (optionnel).'
              : 'Modifiez le nom, la place assignée ou les notes.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">
              Nom manuel <span className="text-red-500">*</span>
            </label>
            <Input
              value={unit.nom_manuel || ''}
              onChange={(e) => onChange({ ...unit, nom_manuel: e.target.value })}
              placeholder="Ex: Remorque Peinture Julie"
              maxLength={200}
              data-testid="input-nom-manuel"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">
              Place de parking <span className="text-slate-400">(texte libre, laissez vide si non assignée)</span>
            </label>
            <Input
              value={unit.place_parking || ''}
              onChange={(e) => onChange({ ...unit, place_parking: e.target.value })}
              placeholder="Ex: 5, A-12, arrière-cour…"
              maxLength={50}
              data-testid="input-place-parking"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">
              Notes <span className="text-slate-400">(optionnel)</span>
            </label>
            <Textarea
              value={unit.notes || ''}
              onChange={(e) => onChange({ ...unit, notes: e.target.value })}
              placeholder="Ex: clé sur crochet A, démarreur capricieux…"
              maxLength={500}
              rows={3}
              data-testid="input-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving} data-testid="btn-cancel-form">
            <X className="w-4 h-4 mr-1.5" />
            Annuler
          </Button>
          <Button onClick={() => onSave(unit)} disabled={saving || !unit.nom_manuel?.trim()} data-testid="btn-save-form">
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? 'Sauvegarde…' : (isNew ? 'Créer' : 'Enregistrer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
